/**
 * PlumeLayer - Custom layer for molecular plume visualization
 * Renders semi-transparent heat clouds for pollutants
 */

import { Source, Layer, type CircleLayer, type HeatmapLayer } from 'react-map-gl';
import type { AirQualityGeoJSON, PollutantType } from '@/types';

interface PlumeLayerProps {
  pollutantType: PollutantType;
  data: AirQualityGeoJSON;
  visible: boolean;
  opacity?: number;
}

// Color configurations for different pollutants
const PLUME_COLORS: Record<PollutantType, { low: string; mid: string; high: string }> = {
  pm25: {
    low: '#00ff88',    // Green
    mid: '#ffdd00',    // Yellow
    high: '#ff3366',   // Red
  },
  pm10: {
    low: '#00ff88',
    mid: '#ff9500',
    high: '#ff3366',
  },
  ozone: {
    low: '#00cce6',    // Cyan
    mid: '#8000ff',    // Purple
    high: '#ff00aa',   // Magenta
  },
  co2: {
    low: '#808080',    // Gray
    mid: '#b3804d',    // Brown
    high: '#4d331a',   // Dark brown
  },
  no2: {
    low: '#66ff66',
    mid: '#ffaa00',
    high: '#ff4444',
  },
  so2: {
    low: '#66ccff',
    mid: '#ff66cc',
    high: '#cc0066',
  },
};

export function PlumeLayer({
  pollutantType,
  data,
  visible,
  opacity = 0.6,
}: PlumeLayerProps) {
  const colors = PLUME_COLORS[pollutantType] || PLUME_COLORS.pm25;
  const sourceId = `plume-source-${pollutantType}`;
  const heatmapLayerId = `plume-heatmap-${pollutantType}`;
  const circleLayerId = `plume-circles-${pollutantType}`;

  // Heatmap layer for smooth plume effect
  const heatmapLayer: HeatmapLayer = {
    id: heatmapLayerId,
    type: 'heatmap',
    source: sourceId,
    paint: {
      // Weight based on concentration value
      'heatmap-weight': [
        'interpolate',
        ['linear'],
        ['get', 'value'],
        0, 0,
        50, 0.5,
        100, 1,
      ],
      // Intensity based on zoom
      'heatmap-intensity': [
        'interpolate',
        ['linear'],
        ['zoom'],
        10, 1,
        15, 3,
      ],
      // Color ramp for the pollutant
      'heatmap-color': [
        'interpolate',
        ['linear'],
        ['heatmap-density'],
        0, 'rgba(0,0,0,0)',
        0.2, colors.low,
        0.5, colors.mid,
        0.8, colors.high,
        1, colors.high,
      ],
      // Radius based on zoom
      'heatmap-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        10, 15,
        15, 30,
        20, 50,
      ],
      // Overall opacity
      'heatmap-opacity': visible ? opacity : 0,
    },
  };

  // Circle layer for individual data points (visible at high zoom)
  const circleLayer: CircleLayer = {
    id: circleLayerId,
    type: 'circle',
    source: sourceId,
    minzoom: 14,
    paint: {
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        14, 4,
        18, 12,
      ],
      'circle-color': [
        'interpolate',
        ['linear'],
        ['get', 'value'],
        0, colors.low,
        50, colors.mid,
        100, colors.high,
      ],
      'circle-opacity': visible ? 0.8 : 0,
      'circle-stroke-width': 1,
      'circle-stroke-color': 'rgba(0, 255, 213, 0.5)',
      'circle-blur': 0.5,
    },
  };

  return (
    <Source id={sourceId} type="geojson" data={data}>
      <Layer {...heatmapLayer} />
      <Layer {...circleLayer} />
    </Source>
  );
}

export default PlumeLayer;
